import {
  STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION,
} from "../../engines/StudioPublicationDescriptor";

import {
  createStudioPublicationAdapter,
} from "../../engines/StudioPublicationAdapter";

import {
  RETAIL_STUDIO_DOMAIN_ID,
  assertRetailStudioDocument,
  cloneRetailStudioDocument,
  isRetailStudioDocument,
} from "./retailStudioDocument";

import {
  createRetailPOSConfiguration,
} from "./retailPOSConfiguration";


export const RETAIL_PUBLICATION_ADAPTER_ID =
  "retail-publication";

export const RETAIL_PUBLICATION_ADAPTER_VERSION =
  "1.0.0";


/**
 * Create the Retail implementation of the generic
 * StudioPublicationAdapter contract.
 *
 * The adapter is intentionally stateless.
 */
export function createRetailPublicationAdapter() {
  return createStudioPublicationAdapter({
    id:
      RETAIL_PUBLICATION_ADAPTER_ID,

    version:
      RETAIL_PUBLICATION_ADAPTER_VERSION,

    domainId:
      RETAIL_STUDIO_DOMAIN_ID,


    supportsDomain(
      domainId
    ) {
      return (
        domainId
        === RETAIL_STUDIO_DOMAIN_ID
      );
    },


    supportsSchemaVersion(
      schemaVersion
    ) {
      return (
        schemaVersion
        === STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION
      );
    },


    migratePublication(
      descriptor
    ) {
      /*
       * LOT 12.5 schema version 1 requires no migration.
       *
       * PublicationRuntimeBridge already provides a defensive
       * descriptor copy before invoking this method.
       */
      return descriptor;
    },


    validatePublication(
      descriptor
    ) {
      const issues = [];

      if (
        descriptor?.domainId
        !== RETAIL_STUDIO_DOMAIN_ID
      ) {
        issues.push(
          createIssue(
            "domainId",
            "INVALID_RETAIL_DOMAIN",
            'Retail publication domainId must be "retail".'
          )
        );
      }

      if (
        descriptor?.schemaVersion
        !== STUDIO_PUBLICATION_DESCRIPTOR_SCHEMA_VERSION
      ) {
        issues.push(
          createIssue(
            "schemaVersion",
            "UNSUPPORTED_SCHEMA_VERSION",
            "Retail publication schemaVersion is unsupported."
          )
        );
      }

      const document =
        extractRetailDocument(
          descriptor?.payload
        );

      if (
        !isRetailStudioDocument(
          document
        )
      ) {
        issues.push(
          createIssue(
            "payload.document",
            "INVALID_RETAIL_DOCUMENT",
            "Retail publication requires a valid RetailStudioDocument."
          )
        );
      }

      return Object.freeze({
        valid:
          issues.length === 0,

        issues:
          Object.freeze(issues),
      });
    },


    deserializePublication(
      descriptor
    ) {
      const document =
        extractRetailDocument(
          descriptor?.payload
        );

      assertRetailStudioDocument(
        document
      );

      return cloneRetailStudioDocument(
        document
      );
    },


    resolveRuntimeConfiguration(
      document,
      descriptor
    ) {
      assertRetailStudioDocument(
        document
      );

      const retail =
        document.extensions
          .retail;

      return createRetailPOSConfiguration({
        publicationId:
          descriptor.publicationId,

        companyUuid:
          descriptor.companyUuid,

        activityUuid:
          descriptor.activityUuid,

        publicationSchemaVersion:
          descriptor.schemaVersion,

        runtimeVersion:
          descriptor.runtimeVersion,

        catalog: {
          layout:
            retail.catalog
              .sections,

          categories:
            retail.catalog
              .categoryShortcuts,

          productShortcuts:
            retail.catalog
              .productShortcuts,
        },

        scannerBindings:
          retail
            .scannerConfiguration,

        terminalBindings:
          retail
            .terminalConfiguration,

        customerActions:
          retail.customerActions,

        loyaltyActions:
          retail.loyaltyActions,

        paymentMethods:
          retail.paymentActions,

        receiptActions:
          retail.receiptActions,

        workspaceLayout: {
          workspaces:
            retail.posWorkspaces,

          navigation:
            retail.navigation,

          layout:
            retail.layout,
        },

        themeReference:
          retail.theme,

        permissionsMetadata:
          retail
            .permissionsMetadata,
      });
    },
  });
}


export const retailPublicationAdapter =
  createRetailPublicationAdapter();


function extractRetailDocument(
  payload
) {
  if (
    payload
    && typeof payload === "object"
    && payload.document
  ) {
    return payload.document;
  }

  return null;
}


function createIssue(
  path,
  code,
  message
) {
  return Object.freeze({
    path,
    code,
    message,
  });
}
